import { memo, useMemo } from 'react';
import { Grid, MenuItem } from '@mui/material';
import { FastField, withFormik } from 'formik';
import * as Yup from 'yup';

import PersianDatePicker from '../../../../components/formik/date-picker/date-picker';
import { TextField } from '../../../../components/formik/text-field';
import {
    isNotInFuture,
    isOver18YearsOld,
    isValidNational,
    isValidNumbers,
    isValidPersianAlpha,
} from 'utils/custom-validation';

const IdentityFields: Array<any> = [
    {
        name: 'name',
        label: 'نام',
        icon: <i className="fa-light fa-user"></i>,
        component: TextField,
        maxLength: 65,
    },
    {
        name: 'family',
        label: 'نام خانوادگی',
        icon: <i className="fa-solid fa-users"></i>,
        component: TextField,
        maxLength: 65,
    },
    {
        name: 'father',
        label: 'نام پدر',
        icon: <i className="fa-solid fa-user-group"></i>,
        component: TextField,
        maxLength: 65,
    },
    {
        name: 'passport',
        label: 'شماره شناسنامه',
        icon: <i className="fa-light fa-passport"></i>,
        ltr: 'true',
        component: TextField,
        numericOnly: true,
        maxLength: 10,
    },
    {
        name: 'national',
        label: 'کد ملی',
        icon: <i className="fa-light fa-id-card"></i>,
        ltr: 'true',
        component: TextField,
        numericOnly: true,
        maxLength: 10,
    },
    {
        name: 'birth',
        label: 'تاریخ تولد',
        icon: <i className="fa-light fa-calendar-days" />,
        showClearBtn: false,
        startOnDay: true,
        ltr: true,
        component: PersianDatePicker,
        minDate: '1200/01/01',
        // Doran defaults to ±30 years. 330 gives ±165 years, so from 1405
        // both 1250 and the older 1240 use case are available in the same year panel.
        yearSpan: 330,
    },
];

const IdentitySchema = Yup.object().shape({
    name: Yup.string()
        .matches(isValidPersianAlpha, 'فقط حروف فارسی مجاز است')
        .required('وارد کردن نام الزامی است')
        .min(3, 'فیلد نام باید حداقل 3 کاراکتر باشد')
        .max(64, 'فیلد نام باید حداکثر 64 کاراکتر باشد')
        .test(
            'no-whitespace-only',
            'فیلد نام نمی‌تواند فقط شامل فاصله باشد',
            (value: any) => value.trim().length > 0,
        ),

    family: Yup.string()
        .matches(isValidPersianAlpha, 'فقط حروف فارسی مجاز است')
        .required('وارد کردن نام خانوادگی الزامی است')
        .min(3, 'فیلد نام خانوادگی باید حداقل 3 کاراکتر باشد')
        .max(64, 'فیلد نام خانوادگی باید حداکثر 64 کاراکتر باشد')
        .test(
            'no-whitespace-only',
            'فیلد نام خانوادگی نمی‌تواند فقط شامل فاصله باشد',
            (value: any) => value.trim().length > 0,
        ),

    father: Yup.string()
        .matches(isValidPersianAlpha, 'فقط حروف فارسی مجاز است')
        .required('وارد کردن نام پدر الزامی است')
        .min(3, 'فیلد نام پدر باید حداقل 3 کاراکتر باشد')
        .max(64, 'فیلد نام پدر باید حداکثر 64 کاراکتر باشد')
        .test(
            'no-whitespace-only',
            'فیلد نام پدر نمی‌تواند فقط شامل فاصله باشد',
            (value: any) => value.trim().length > 0,
        ),

    passport: Yup.string()
        .required('وارد کردن شماره شناسنامه الزامی است')
        .max(10, 'فیلد شماره شناسنامه باید حداکثر 10 کاراکتر باشد')
        .matches(
            isValidNumbers,
            'فیلد شماره شناسنامه باید فقط شامل اعداد باشد',
        ),

    national: Yup.string()
        .required('وارد کردن کد ملی الزامی است')
        .length(10, 'فیلد کد ملی باید دقیقاً 10 کاراکتر باشد')
        .test('is-valid-national-code', 'کد ملی وارد شده معتبر نیست', (value) =>
            isValidNational(value),
        ),

    birth: Yup.string()
        .required('وارد کردن تاریخ تولد الزامی است')
        .max(64, 'فیلد تاریخ تولد باید حداکثر 64 کاراکتر باشد')
        .test(
            'is-not-in-future',
            'تاریخ انتخاب شده نباید بعد از امروز باشد',
            isNotInFuture,
        )
        .test(
            'is-over-18-years-old',
            'سن باید بیش از ۱۸ سال باشد',
            isOver18YearsOld,
        ),
});

const IdentityFormTemplate = memo((props: any): JSX.Element => {
    const disableAutoFill = props?.disableAutoFill ?? false;
    const disableBirthDate = props?.disableBirthDate ?? false;
    const initialBirthPickerView = props?.initialBirthPickerView;
    const fields = useMemo(() => IdentityFields, []);

    return (
        <Grid container spacing={2}>
            {fields.map((field) => {
                const datePickerProps =
                    field.name === 'birth' && initialBirthPickerView
                        ? { initialPickerView: initialBirthPickerView }
                        : {};

                return (
                    <Grid item xs={12} md={6} key={field.name}>
                        <FastField
                            component={field.component}
                            select={field?.options ? true : false}
                            {...field}
                            disabled={
                                field.name === 'birth'
                                    ? disableBirthDate || field.disabled
                                    : field.disabled
                            }
                            {...datePickerProps}
                            disableAutoFill={disableAutoFill}>
                            {field.options?.map((menu: any) => (
                                <MenuItem value={menu.value} key={menu.value}>
                                    {menu.label}
                                </MenuItem>
                            ))}
                        </FastField>
                    </Grid>
                );
            })}
        </Grid>
    );
});

const IdentityObject = (identityObj?: any) => {
    return {
        name: '',
        family: '',
        father: '',
        passport: '',
        national: '',
        birth: '',
        ...identityObj,
    };
};

const IdentityForm = withFormik({
    mapPropsToValues: () => IdentityObject(),
    validationSchema: IdentitySchema,
    validateOnChange: false,
    validateOnBlur: true,
    handleSubmit: (values, { setSubmitting }) => {},
})(IdentityFormTemplate);

export {
    IdentityForm,
    IdentityFormTemplate,
    IdentityObject,
    IdentitySchema,
    IdentityFields,
};
